/**
 * Compact task card for Kanban board columns.
 *
 * Layout:
 *   Line 1: [status dot] Title
 *   Line 2: ticket # · due date · agent · comment count
 *
 * Left border = priority color. No status/priority text (column and border convey those).
 * Draggable by the pointer (`useKanbanCardDrag`). Every card is draggable —
 * including blocked tasks and tasks with unfinished dependencies — and a
 * person may drop it on any column.
 */

import { useCallback, useState } from "react";
import { cn } from "@/lib/cn";
import { StatusDot } from "@/components/ui";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTaskSignals } from "./use-task-signals";
import {
  taskStateTone,
  taskStateText,
  taskAssigneeName,
  taskPullRequestText,
} from "./task-state";
import { useFrameField } from "@/stores/frames-selectors";
import { previewSource } from "@/lib/frame-preview-source";
import type { Task, TaskStatus } from "@/api/tasks";
import { docPlainText } from "@/components/shapes/doc-frame/doc-content";
import { DelegationRollupChip } from "./delegation-rollup-chip";
import { DelegationChildList } from "./delegation-child-list";
import { TaskParentBreadcrumb } from "./task-parent-breadcrumb";
import { TaskProjectChip } from "./task-metadata-chips";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import {
  useKanbanCardDrag,
  type KanbanCardDragHandlers,
} from "./use-kanban-card-drag";

interface KanbanCardProps {
  task: Task;
  onSelect: (taskId: string) => void;
  onDragStartTask?: (task: Task) => void;
  onDragOverStatus?: (status: TaskStatus | null) => void;
  /** The drag ended over `status`, or was cancelled (`null`). */
  onDragEndTask?: (task: Task, status: TaskStatus | null) => void;
}

export function KanbanCard({
  task,
  onSelect,
  onDragStartTask,
  onDragOverStatus,
  onDragEndTask,
}: KanbanCardProps) {
  const t = useT(framesTaskboardMessages);
  const [rollupExpanded, setRollupExpanded] = useState(false);
  const childSummary = task.child_tasks_summary;
  const hasChildren = !!childSummary && childSummary.count > 0;
  const parentSummary = task.parent_task_summary;
  const signals = useTaskSignals();
  const tone = taskStateTone(task, {
    question: signals.questionTaskIds.has(task.id),
    running: signals.runningTaskIds.has(task.id),
  });
  const selected = useTaskPocketStore((state) => state.taskId === task.id);
  const due = task.due_at ? new Date(task.due_at) : null;
  const now = new Date();
  const tomorrow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
  );
  const dueLabel =
    due?.toDateString() === now.toDateString()
      ? t("today")
      : due?.toDateString() === tomorrow.toDateString()
        ? t("tomorrow")
        : null;
  const metadata = [
    task.ticket_display,
    taskAssigneeName(task),
    dueLabel,
    taskPullRequestText(task),
  ]
    .filter(Boolean)
    .join(" ");

  // Note preview: look up linked frame content for note-sourced tasks
  const notePreview = useFrameField(
    task.source === "note" ? task.frame : null,
    (frame) => {
      if (!frame) return null;
      const preview = docPlainText(frame.frameType, previewSource(frame));
      if (!preview) return null;
      return preview.split("\n")[0]?.slice(0, 120) ?? null;
    },
  );

  const dragHandlers: KanbanCardDragHandlers = {
    onDragStart: () => onDragStartTask?.(task),
    onDragOver: (status) => onDragOverStatus?.(status),
    onDragEnd: (status) => onDragEndTask?.(task, status),
  };
  const drag = useKanbanCardDrag(dragHandlers);

  const handleClick = useCallback(() => {
    onSelect(task.id);
  }, [task.id, onSelect]);

  return (
    <div
      className={cn(
        "kanban-card group rounded-preview px-2 py-2 transition-colors hover:bg-[var(--color-interactive-hover)]",
        selected && "bg-[var(--color-surface-selected)]",
        "cursor-grab select-none active:cursor-grabbing",
      )}
      onPointerDown={drag.onPointerDown}
      onClick={() => {
        if (!drag.consumeClick()) handleClick();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (
          event.target === event.currentTarget &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          handleClick();
        }
      }}
      data-task-id={task.id}
      data-task-pocket-item={task.id}
      data-testid={`kanban-card-${task.id}`}
    >
      {/* Parent breadcrumb — only when this card is a delegation child. */}
      {parentSummary && (
        <TaskParentBreadcrumb parent={parentSummary} onOpenParent={onSelect} />
      )}

      <div className="flex items-start gap-2">
        <p className="line-clamp-3 min-w-0 flex-1 text-appkit-body font-medium text-primary">
          {task.title}
        </p>
        {tone && <StatusDot tone={tone} label={taskStateText(task, tone)} />}
      </div>
      {metadata && (
        <p className="mt-1 text-appkit-small font-medium text-muted">
          {metadata}
        </p>
      )}
      <TaskProjectChip
        projectId={task.project}
        className="mt-1 flex text-appkit-small"
      />
      {notePreview && (
        <p
          className="mt-1 truncate text-appkit-small text-muted"
          data-testid={`kanban-card-note-preview-${task.id}`}
        >
          {notePreview}
        </p>
      )}
      {/* Delegation rollup: chip summarizing child tasks, expandable inline. */}
      {hasChildren && childSummary && (
        <>
          <DelegationRollupChip
            counts_by_status={childSummary.counts_by_status}
            totalCount={childSummary.count}
            expanded={rollupExpanded}
            onToggle={() => setRollupExpanded((v) => !v)}
          />
          {rollupExpanded && (
            <DelegationChildList
              children={childSummary.children}
              onChildClick={onSelect}
            />
          )}
        </>
      )}
    </div>
  );
}
